import {
  columnRowGroupEventSourceTypes,
  gfpaDetailsGroupsColumnsFields,
} from '@/constants/gfpa.constant'
import { gfrDetailsDefaultGroupingFields } from '@/constants/gfr.constant'
import { rvDetailsDefaultGroupingFields } from '@/constants/rv.constant'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { IGfpaDetailsDTO } from '@/interfaces/gfpaReport.interface'
import { IGfrDetailsDTO } from '@/interfaces/gfrReport.interface'
import { sideBarDefaultConfig } from '@/utils/grid-utils/ag-grid-config'
import { reportDefaultColDef } from '@/utils/grid-utils/colDefs'
import { getColumnDetail } from '@/utils/grid-utils/columnUtils'
import { formatCellValue } from '@/utils/tableGridUtils'
import {
  ColDef,
  ColGroupDef,
  Column,
  ColumnRowGroupChangedEvent,
  ColumnState,
  GridReadyEvent,
  ModuleRegistry,
  RowGroupOpenedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import { IRvSecurityDetailsData } from '@interfaces/rvDetails.interface'
import React, { useCallback, useMemo, useRef } from 'react'

export type ISecurityDetailsTable = {
  rowData: IGfpaDetailsDTO[] | IGfrDetailsDTO[] | IRvSecurityDetailsData[]
  setDetailsTableColumns: Function
  setGridReference: Function
  detailsColDef: (ColDef | ColGroupDef)[]
  onExpandRowGroup: Function
  onColumnRowGroupSelectionChanged: Function
  reportType: string
}

ModuleRegistry.registerModules([
  CsvExportModule,
  ExcelExportModule,
  RowGroupingModule,
])

export const GFPADetailsTable: React.FC<ISecurityDetailsTable> = React.memo(
  ({
    rowData,
    setDetailsTableColumns,
    setGridReference,
    detailsColDef,
    onExpandRowGroup,
    onColumnRowGroupSelectionChanged,
    reportType,
  }) => {
    const themeClassName = 'ag-theme-quartz'
    const gridRef = useRef<AgGridReact>(null)
    const parentNodes: RowNode[] = []

    const onGridReady = (params: GridReadyEvent) => {
      if (document.body.clientWidth > 1400) {
        params.api.sizeColumnsToFit()
      } else {
        params.api.autoSizeAllColumns()
      }
      setGridReference(gridRef)
      if (rowData.length) {
        let defaultGroupingFieldName: string[]

        switch (reportType) {
          case AxiomReportType.GFPA:
          case AxiomReportType.GFPAH:
            defaultGroupingFieldName = gfpaDetailsGroupsColumnsFields
            break
          case AxiomReportType.GFR:
            defaultGroupingFieldName = gfrDetailsDefaultGroupingFields
            break
          case AxiomReportType.RV:
            defaultGroupingFieldName = rvDetailsDefaultGroupingFields
            break
          default:
            defaultGroupingFieldName = []
            break
        }
        params.api.setRowGroupColumns(defaultGroupingFieldName)
      }
    }

    const handleRowGroupOpened = useCallback((event: RowGroupOpenedEvent) => {
      const { node } = event
      if (node.expanded) {
        parentNodes.length = 0
        let currentNode = node as RowNode | null
        while (currentNode) {
          currentNode.key && parentNodes.push(currentNode)
          currentNode = currentNode.parent as RowNode | null
        }
        gridRef.current?.api.forEachNode((rowNode) => {
          if (
            !parentNodes.includes(rowNode as RowNode) &&
            rowNode.expanded &&
            node.key
          ) {
            rowNode.setExpanded(false)
          }
        })
        updateExpandRowGroup()
      } else {
        const collapsedNodeIndex = parentNodes.findIndex(
          (nodeData: RowNode) => {
            return nodeData.key === node.key
          },
        )
        if (collapsedNodeIndex !== -1) {
          if (parentNodes[collapsedNodeIndex].level === 0) {
            onExpandRowGroup({
              name: '',
              level: 0,
            })
          } else {
            parentNodes.splice(collapsedNodeIndex, 1)
            updateExpandRowGroup()
          }
        }
      }
    }, [])
    const updateExpandRowGroup = () => {
      if (parentNodes.length === 1) {
        const key = parentNodes[0].key
        onExpandRowGroup({
          name: key + '_' + key,
          level: parentNodes[0].level,
        })
      }
    }

    const onColumnRowGroupChanged = (event: ColumnRowGroupChangedEvent) => {
      const groupedColumns = event.api.getRowGroupColumns()
      const newColumnId =
        event.column?.getColId() || groupedColumns[0]?.getColId()
      if (
        groupedColumns.length &&
        newColumnId &&
        columnRowGroupEventSourceTypes.includes(event.source)
      ) {
        event.api.setRowGroupColumns([newColumnId])
        const newColumnState = detailsColDef.map(
          (colDef: ColDef | ColGroupDef) => {
            if (
              groupedColumns.some(
                (col) => col.getColId() === (colDef as ColDef).field,
              )
            ) {
              return {
                ...colDef,
                colId: (colDef as ColDef).field as string,
                hide: true,
              }
            }
            return { ...colDef }
          },
        )
        setDetailsTableColumns(newColumnState as (ColDef | ColGroupDef)[])
        event.api.applyColumnState({
          state: newColumnState as ColumnState[],
        })
        onColumnRowGroupSelectionChanged(newColumnId)
      } else if (!groupedColumns.length) {
        onColumnRowGroupSelectionChanged(null)
      }
    }

    const autoGroupColumnDef = useMemo<ColDef>(() => {
      return {
        headerTooltip: 'Group',
        minWidth: 190,
        pinned: 'left',
        lockPinned: true,
        tooltipValueGetter: (params) => {
          if (params.node?.group) {
            const count = params.node.allChildrenCount
            const groupFieldValue = params.node.key
            // let dateFilterFields: string[]

            // switch (reportType) {
            //   case AxiomReportType.GFPA:
            //     dateFilterFields = gfpaDetailsDateFilterFields
            //     break
            //   case AxiomReportType.GFR:
            //     dateFilterFields = gfrDetailsDateFilterFields
            //     break
            //   case AxiomReportType.RV:
            //     dateFilterFields = rvDetailsDateFilterFields
            //     break
            //   default:
            //     dateFilterFields = []
            //     break
            // }
            const value = formatCellValue(
              groupFieldValue,
              getColumnDetail(String(params.node.field), reportType).format,
            )
            // dateFilterFields.includes(params.node.field as string) &&
            // dayjs(groupFieldValue).isValid()
            //   ? dayjs(groupFieldValue).format(DATE_FORMAT.TABLE_CHART)
            //   : groupFieldValue
            return `${value} (${count})`
          }
          return params.value
        },
      }
    }, [])
    return (
      <div
        className={`${themeClassName} ${reportType?.toLowerCase()}-details-table-container`}
        style={{ height: '100%' }}
      >
        <AgGridReact
          ref={gridRef}
          onGridReady={onGridReady}
          defaultColDef={{ ...reportDefaultColDef, enableRowGroup: true }}
          rowGroupPanelShow={'always'}
          suppressAggFuncInHeader={true}
          suppressDragLeaveHidesColumns={true}
          columnDefs={detailsColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          rowData={rowData}
          groupDefaultExpanded={0}
          sideBar={sideBarDefaultConfig}
          // groupHeaderHeight={24}
          // pivotGroupHeaderHeight={24}

          // headerHeight={24}
          onColumnVisible={(params) => {
            if (params.source === 'toolPanelUi') {
              const columns = params.columns as Column[]
              const newColumnState = detailsColDef.map(
                (colDef: ColDef | ColGroupDef) => {
                  if (
                    columns.some(
                      (col) => col.getColId() === (colDef as ColDef).field,
                    )
                  ) {
                    return { ...colDef, hide: !params.visible }
                  } else {
                    return { ...colDef }
                  }
                },
              )
              setDetailsTableColumns(newColumnState as (ColDef | ColGroupDef)[])
            }
          }}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          onRowGroupOpened={handleRowGroupOpened}
          data-cy={`${reportType?.toLowerCase()}-details-table`}
        />
      </div>
    )
  },
)
